import { AxiosInstance, AxiosResponse } from 'axios'
import { inject } from 'tsyringe'
import { httpToken } from '~/constants/dependency-injection/tokens'
import VueRouter from 'vue-router'
import { containerScoped } from '~/decorators/dependency-container'
import { invalidBodyError } from '~/services/errors'
import { toCamelCase } from '~/utils/object'
import VueI18n from 'vue-i18n'
import LoggerService from '~/services/LoggerService'
import {
  AutoTouchAvailability,
  AutoTouchAvailabilityRaw,
  AutoTouchScheduleRaw,
  AutoTouchSchedule,
  AutoTouchHistory,
  AutoTouchDate,
  AutoTouchOneClick,
  AutoTouchOneClickStripeDetails
} from '~/models/auto-touch/types'
import { LegacyUrlService } from '~/services/legacy/url/LegacyUrlService'
import RequestBuilder from '~/builders/http/RequestBuilder'
import { USER_NS } from '~/store/modules/shared/user/state'
import { Store } from 'vuex'
import { RootState } from '~/store/types'

@containerScoped()
export default class AutoTouchService {
  constructor(
    @inject(httpToken) private http: AxiosInstance,
    @inject(LoggerService) public logger: LoggerService,
    @inject(VueI18n) private i18n: VueI18n,
    @inject(VueRouter) private router: VueRouter,
    @inject(LegacyUrlService) private legacyUrlService: LegacyUrlService,
    @inject(Store) private store: Store<RootState>,
    @inject(RequestBuilder) private requestBuilder: RequestBuilder
  ) {}

  async fetchAutoTouchScheduler(
    classifiedId: number
  ): Promise<{
    availability: Array<
      { [k in keyof AutoTouchAvailabilityRaw]: AutoTouchAvailability } & {
        formatedHour: string
      }
    >
    schedule: AutoTouchSchedule
    totalCostForAllClassifieds: number
    previousSchedules: { [key: string]: boolean }
  }> {
    const { query } = this.router.currentRoute
    const test = query.test === '1' ? '?test=1' : ''
    const response: AxiosResponse = await this.http.get(
      `/api/classifieds/${classifiedId}/auto-touch/${test}`
    )

    const body = toCamelCase(response.data)
    if (!body?.data?.availability) {
      throw invalidBodyError(body)
    }

    const { availability } = body.data
    const schedule = body.data?.schedule ? body.data.schedule.schedule : []
    const history = body.data?.schedule ? body.data.schedule.history : []
    const normalizedData = this.normalizedAvailabilityData(
      availability,
      schedule,
      history
    )

    return {
      availability: normalizedData,
      schedule: body.data.schedule || {},
      totalCostForAllClassifieds: body.data?.totalCostForAllClassifieds,
      previousSchedules: body.data?.previousSchedules?.length
        ? body.data.previousSchedules[0]
        : []
    }
  }

  normalizedAvailabilityData(
    availability: AutoTouchAvailabilityRaw,
    schedule: Array<AutoTouchScheduleRaw> = [],
    history: Array<AutoTouchHistory> = []
  ): Array<
    { [k in keyof AutoTouchAvailabilityRaw]: AutoTouchAvailability } & {
      formatedHour: string
    }
  > {
    const hours = availability.monday.map(d => d.hour)

    const orderedAvailability = {
      monday: availability.monday,
      tuesday: availability.tuesday,
      wednesday: availability.wednesday,
      thursday: availability.thursday,
      friday: availability.friday,
      saturday: availability.saturday,
      sunday: availability.sunday
    }
    const days = Object.keys(orderedAvailability)
    const historyObject: {
      [key: string]: Array<{ status: string; date: string }>
    } = {}
    history.forEach(t => {
      if (!Array.isArray(historyObject[t.day + t.hour])) {
        historyObject[t.day + t.hour] = []
      }
      historyObject[t.day + t.hour].push({ status: t.status, date: t.date })
    })

    return hours.map((hour: string, i: number) => {
      const formatedHour = this.normalizeHours(parseInt(hour, 10))
      const availabilityData: any = { formatedHour }
      days.forEach(d => {
        availabilityData[d] = {
          formatedHour,
          hour,
          isSelected: schedule.some(s => s.day === d && s.hour === hour),
          date: d,
          cost: availability[d as keyof AutoTouchAvailabilityRaw][i].cost,
          history: historyObject[d + hour]
        }
      })
      return availabilityData
    })
  }

  normalizeHours(hour: number): string {
    if (hour < 10) {
      return `0${hour}:00`
    }
    return `${hour}:00`
  }

  async scheduleAutoTouch(
    classifiedId: number,
    scheduleData: Array<any>
  ): Promise<AutoTouchSchedule> {
    const response: AxiosResponse = await this.http.post(
      `/api/classifieds/${classifiedId}/auto-touch/`,
      {
        schedule: scheduleData
      }
    )

    const body = response.data
    if (!body?.data?.schedule) {
      throw invalidBodyError(body)
    }
    return toCamelCase(body.data.schedule)
  }

  async massScheduleAutoTouch(
    classifieds: number[],
    scheduleData: Array<any>
  ): Promise<AutoTouchSchedule> {
    const response: AxiosResponse = await this.http.post(
      '/api/classifieds/auto-touch/',
      {
        schedule: scheduleData,
        classifieds
      }
    )

    const body = response.data
    if (!body?.data?.schedule) {
      throw invalidBodyError(body)
    }
    return toCamelCase(body.data.schedule)
  }

  async massDeleteScheduleAutoTouch(
    classifieds: number[]
  ): Promise<AutoTouchSchedule> {
    const response: AxiosResponse = await this.http.delete(
      '/api/classifieds/auto-touch/',
      {
        data: { classifieds }
      }
    )
    const body = response.data
    if (!body?.message) {
      throw invalidBodyError(body)
    }
    return body.message
  }

  async renewExpire(
    classifiedId: number
  ): Promise<{ touchedCount: number; expires: string }> {
    const response: AxiosResponse = await this.http.patch(
      `/api/classifieds/${classifiedId}/auto-touch/`
    )
    const body = toCamelCase(response.data)
    if (!body?.data?.schedule) {
      throw invalidBodyError(body)
    }
    const { touchedCount, expires } = body.data.schedule
    return { touchedCount, expires }
  }

  async deleteSchedule(classifiedId: number): Promise<string> {
    const response: AxiosResponse = await this.http.delete(
      `/api/classifieds/${classifiedId}/auto-touch/`
    )
    const body = response.data
    if (!body?.message) {
      throw invalidBodyError(body)
    }

    return body.message
  }

  fetchOneClickAutoTouchData(classifiedId: number): Promise<AutoTouchOneClick> {
    return this.requestBuilder
      .request('get', `/api/classifieds/${classifiedId}/auto-touch/oneclick/`)
      .validate(
        body => body && body.data && (body.data.price || body.data.price === 0)
      )
      .send()
  }

  getOneClickDetails({
    classifiedID,
    payment,
    cancelUrl
  }: {
    classifiedID: number
    payment?: 'stripe' | 'credits'
    cancelUrl?: string
  }): Promise<AutoTouchOneClickStripeDetails> {
    const baseUrl = process.client
      ? window.location.origin
      : `https://${process.env.DOMAIN}`

    const classifiedUrl = this.router.resolve({
      name: '__classifieds_view',
      params: { slugOrId: classifiedID.toString() }
    }).href

    const serverCancelUrl = cancelUrl || baseUrl + classifiedUrl
    const clientCancelUrl = cancelUrl || window.location.href

    const finalCancelUrl = process.client ? clientCancelUrl : serverCancelUrl

    let successUrl = ''
    if (this.store.getters[`${USER_NS}/isSingle`]) {
      successUrl = baseUrl + classifiedUrl
    } else {
      successUrl =
        baseUrl +
        this.router.resolve({
          name: '__account_settings_payments_success',
          query: { one_click_auto_touch_clsfd: classifiedID.toString() }
        }).href
    }

    return this.requestBuilder
      .request('post', `/api/classifieds/${classifiedID}/auto-touch/oneclick/`)
      .data({
        cancel_url: finalCancelUrl,
        success_url: successUrl,
        payment
      })
      .validate(body => {
        return payment === 'credits' ? body?.message : body?.data?.url
      })
      .send()
  }

  cancelOneClickAutotouch(classifiedId: number) {
    return this.requestBuilder
      .request(
        'delete',
        `/api/classifieds/${classifiedId}/auto-touch/oneclick/`
      )
      .validate(body => body?.data?.message)
      .send()
  }

  schedulerText(autoTouch: AutoTouchSchedule): string {
    const autoTouchCamelCase = toCamelCase(autoTouch)
    const scheduleDays = {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: []
    } as any
    const days = Object.keys(scheduleDays)
    let scheduleString = ''

    const { schedule, lastTouchError } = autoTouchCamelCase

    let totalCost = 0
    schedule.forEach((s: AutoTouchScheduleRaw) => {
      const hour = parseInt(s.hour, 10) < 10 ? `0${s.hour}` : s.hour
      scheduleDays[s.day].push(`${hour}:00`)
      totalCost += s.cost as number
    })

    days.forEach(d => {
      if (scheduleDays[d].length) {
        scheduleString +=
          `${(this.i18n.t(d) as string).slice(0, 3)}: ${scheduleDays[d]
            .slice(0, 3)
            .join(', ')}` + (scheduleDays[d].length > 3 ? '...<br> ' : '<br> ')
      }
    })

    let html = ''

    if (lastTouchError) {
      html += `<b style="color:red">${lastTouchError}</b><br><a href="${this.legacyUrlService.getBuyCreditsUrl()}">${this.i18n.t(
        'buy credits::promo'
      )}</a><br><hr>`
    } else if (totalCost) {
      html += `${this.i18n.t(
        'total_schedule_cost'
      )} <b>${totalCost} credits</b><hr>`
    }

    html += `<b>${this.i18n.t('schedule::auto_touch')}</b>
     <div>${scheduleString}</div>
    `

    return html
  }

  generateHours(): Array<AutoTouchDate> {
    return new Array(24)
      .fill('')
      .map((_, i) => ({ hour: i.toString(), cost: 0 }))
  }

  getAvailabilityForMassAcions() {
    return this.normalizedAvailabilityData({
      monday: this.generateHours(),
      tuesday: this.generateHours(),
      friday: this.generateHours(),
      wednesday: this.generateHours(),
      thursday: this.generateHours(),
      sunday: this.generateHours(),
      saturday: this.generateHours()
    })
  }
}
